import React, { useState, useEffect } from 'react';
import './QRTableManagement.css';

const QRTableManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Floor management state (replicate waiter assignment logic)
  const [floorCount, setFloorCount] = useState(1);
  const [floorAssignments, setFloorAssignments] = useState([]);
  const [totalTables, setTotalTables] = useState(20);
  
  // New Table Modal state
  const [showNewTableModal, setShowNewTableModal] = useState(false);
  const [newTableData, setNewTableData] = useState({
    tableName: '',
    fromTable: '',
    toTable: ''
  });

  // Initialize floor assignments when floor count changes
  useEffect(() => {
    const tablesPerFloor = Math.floor(totalTables / floorCount);
    const newAssignments = [];
    
    for (let i = 0; i < floorCount; i++) {
      const fromTable = i * tablesPerFloor + 1;
      const toTable = i === floorCount - 1 
        ? totalTables 
        : (i + 1) * tablesPerFloor;
      
      newAssignments.push({
        floorName: floorAssignments[i]?.floorName || `Floor ${i + 1}`,
        fromTable: floorAssignments[i]?.fromTable || fromTable,
        toTable: floorAssignments[i]?.toTable || toTable
      });
    }
    
    setFloorAssignments(newAssignments);
  }, [floorCount, totalTables]);

  // removed unused handleFloorCountChange to satisfy linter

  const updateFloorAssignment = (index, field, value) => {
    const updated = [...floorAssignments];
    if (field === 'fromTable' || field === 'toTable') {
      // Allow empty string for editing, otherwise convert to number
      if (value === '') {
        updated[index][field] = '';
      } else {
        const numValue = parseInt(value);
        if (!isNaN(numValue)) {
          updated[index][field] = numValue;
        }
      }
    } else {
      updated[index][field] = value;
    }
    setFloorAssignments(updated);
  };

  const saveFloorAssignments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch('/api/floors/assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          floors: floorAssignments
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save floor assignments');
      }

      alert('✅ Floor assignments saved successfully');
    } catch (err) {
      console.error('Error saving floor assignments:', err);
      alert(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewTable = async () => {
    if (!newTableData.tableName || !newTableData.fromTable || !newTableData.toTable) {
      alert('Please fill all fields');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch('/api/tables/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newTableData.tableName,
          fromTable: parseInt(newTableData.fromTable),
          toTable: parseInt(newTableData.toTable)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add new table');
      }

      alert('✅ Table added successfully');
      setShowNewTableModal(false);
      setNewTableData({ tableName: '', fromTable: '', toTable: '' });
    } catch (err) {
      console.error('Error adding table:', err);
      alert(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="qr-table-management">
      {/* Error Message */}
      {error && (
        <div className="error-banner">❌ {error}</div>
      )}

      <div className="floor-form">
        <div>
          <h4 className="floor-section-title">
            Assign Tables to Floors
          </h4>
          {floorAssignments.map((floor, idx) => (
            <div key={idx} className="floor-assignment-card">
              {/* Table Summary as Heading at Top */}
              <div className="floor-table-summary">
                Tables: {floor.fromTable} - {floor.toTable} ({Math.max(0, floor.toTable - floor.fromTable + 1)} tables)
              </div>
              
              {/* Floor Name - Label on left, input on right */}
              <div className="floor-assignment-row">
                <label style={{color: '#f3f4f6', fontSize: '14px', whiteSpace: 'nowrap'}}>
                  Floor Name:
                </label>
                <input
                  type="text"
                  value={floor.floorName}
                  onChange={(e) => updateFloorAssignment(idx, 'floorName', e.target.value)}
                  placeholder={`Floor ${idx + 1}`}
                  className="floor-name-input"
                />
              </div>
              
              {/* From Table and To Table in same line */}
              <div className="floor-table-row" style={{display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'flex-start'}}>
                <div className="floor-table-group" style={{display: 'flex', flexDirection: 'row', gap: '12px', flex: 1, alignItems: 'center'}}>
                  <label style={{color: '#f3f4f6', fontSize: '14px', whiteSpace: 'nowrap'}}>
                    From Table:
                  </label>
                  <input
                    type="number"
                    max={totalTables}
                    value={floor.fromTable}
                    style={{
                      background: '#111827',
                      border: '3px solid #ffffff',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      color: '#f9fafb',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      flex: 1,
                      width: '100%',
                      boxSizing: 'border-box',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateFloorAssignment(idx, 'fromTable', val);
                    }}
                    onKeyDown={(e) => {
                      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                        return;
                      }
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
                
                <div className="floor-table-group" style={{display: 'flex', flexDirection: 'row', gap: '12px', flex: 1, alignItems: 'center'}}>
                  <label style={{color: '#f3f4f6', fontSize: '14px', whiteSpace: 'nowrap'}}>
                    To Table:
                  </label>
                  <input
                    type="number"
                    max={totalTables}
                    value={floor.toTable}
                    style={{
                      background: '#111827',
                      border: '3px solid #ffffff',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      color: '#f9fafb',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      flex: 1,
                      width: '100%',
                      boxSizing: 'border-box',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateFloorAssignment(idx, 'toTable', val);
                    }}
                    onKeyDown={(e) => {
                      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                        return;
                      }
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="floor-button-group">
          <button
            onClick={() => setShowNewTableModal(true)}
            className="floor-btn-new-table"
          >
            + New Floor
          </button>
          <button
            onClick={saveFloorAssignments}
            className="floor-btn-save"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* New Table Modal */}
      {showNewTableModal && (
        <div className="new-table-modal-overlay" onClick={() => setShowNewTableModal(false)}>
          <div className="new-table-modal" onClick={e => e.stopPropagation()}>
            <div className="new-table-modal-header">
              <h3>add new floor</h3>
            </div>
            
            <div className="new-table-modal-body">
              <div className="new-table-row">
                <label>Enter Floor Name:</label>
                <input
                  type="text"
                  value={newTableData.tableName}
                  onChange={(e) => setNewTableData({...newTableData, tableName: e.target.value})}
                  placeholder="enter the floor here"
                  className="new-table-input"
                />
              </div>
              
              <div className="new-table-inputs-row">
                <div className="new-table-input-group">
                  <label>From Table:</label>
                  <input
                    type="number"
                    value={newTableData.fromTable}
                    onChange={(e) => setNewTableData({...newTableData, fromTable: e.target.value})}
                    placeholder=""
                    className="new-table-number-input"
                  />
                </div>
                
                <div className="new-table-input-group">
                  <label>To Table:</label>
                  <input
                    type="number"
                    value={newTableData.toTable}
                    onChange={(e) => setNewTableData({...newTableData, toTable: e.target.value})}
                    placeholder=""
                    className="new-table-number-input"
                  />
                </div>
              </div>
              
              <div className="new-table-modal-actions">
                <button 
                  onClick={() => setShowNewTableModal(false)}
                  className="new-table-btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddNewTable}
                  className="new-table-btn-add"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Table'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRTableManagement;
